import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import type { Document as ContentfulDocument } from '@contentful/rich-text-types';
import {
  BundleCategoryKey,
  CATEGORY_ORDER,
  MINIMUM_PRODUCTS_IN_BUNDLE,
  PRIORITISATION_RANK,
} from 'components/dynamic-bundle/constants';
import COMMON_CATEGORIES from 'config/constants/common-categories';
import { type BundleCompositionDictionary, getDogTargetWeight } from 'constants/dynamic-bundle';
import { BreedSegmentationIds } from 'constants/segmentation';
import type { Entry } from 'contentful';
import { CartBundle } from 'contexts/type/cart-type';
import { PdProductItem } from 'interfaces/line-item';
import {
  AvailableVariant,
  CollectionProduct,
  CollectionVariant,
} from 'models/collection/collection';
import { ReactNode } from 'react';
import { isBundleProduct } from 'utils/product/is-bundle-product';
import { PetType } from 'constants/pet-type';
const { wetFood, dryFood, snacks } = COMMON_CATEGORIES;

export type ExtendedVariant = CollectionVariant & {
  productTitle: string | ReactNode;
  handle: string;
};

export type BundleVariant = ExtendedVariant & {
  bundleRefs: CartBundle[];
  category: BundleCategoryKey;
};

export type BundleComposition = {
  [key in BundleCategoryKey]: BundleVariant[];
};

type AnySection = Entry<Record<string, unknown>>;

type SectionTextLike = AnySection & {
  sys: AnySection['sys'] & { contentType: { sys: { id: 'sectionText' } } };
  fields: Record<string, unknown> & {
    productInformationTab?: boolean;
    richTitle?: string;
    text?: ContentfulDocument;
  };
};

/**
 * creates bundle composition by the selected food types based on category order
 */
export const createBundleComposition = (productTypes: string[]): Partial<BundleComposition> => {
  const result: Partial<BundleComposition> = {};

  for (const foodCategory of CATEGORY_ORDER) {
    if (!productTypes.includes(foodCategory)) continue;
    result[foodCategory] = [];
  }

  return result;
};

// so a single can ranks below a same-gram multipack (85g vs 36×85g)
const totalWeight = ({ weight, packQuantity }: AvailableVariant): number =>
  weight * (packQuantity ?? 1);

const sortByWeight = (a: AvailableVariant, b: AvailableVariant): number => {
  return totalWeight(a) - totalWeight(b);
};

// a missing weight would make the distance NaN and the comparator non-transitive
const distanceToTarget = ({ weight }: AvailableVariant, targetWeightGrams: number): number =>
  Number.isFinite(weight) ? Math.abs(weight - targetWeightGrams) : Number.MAX_SAFE_INTEGER;

const aimForTargetWeight =
  (targetWeightGrams: number) =>
  (a: AvailableVariant, b: AvailableVariant): number =>
    distanceToTarget(a, targetWeightGrams) - distanceToTarget(b, targetWeightGrams) ||
    sortByWeight(a, b);

interface CalculateBundleArgs {
  products: CollectionProduct[];
  productTypes: string[];
  bundleCompositionDictionary: BundleCompositionDictionary;
  petType: PetType;
  breed?: BreedSegmentationIds;
}

export interface Bundle {
  bundleProducts: Partial<BundleComposition>;
  sumOfProducts: number;
  dictionary: BundleCompositionDictionary;
  bundleId: string;
}

/**
 * Based on sorted results it changes dictionary to the minimum amount available per category and counts,
 * how many products are in such "bundle";

 * Example:
 * default dictionary: wetFood=4, dryFood=1, snacks=3
 * available products: wetFood=5, dryFood=1, snacks=2
 * normalized result: wetFood=4, dryFood=1, snacks=2, productsCount=7
 */
export const normalizeToMinimumAvailable = ({
  bundle,
}: {
  bundle: Bundle;
}): { dictionary: BundleCompositionDictionary; productsCount: number } => {
  const dictionary = structuredClone(bundle.dictionary);
  let totalProductsCount = 0;

  for (const category of CATEGORY_ORDER) {
    const productsCount = Math.min(
      dictionary[category].minAmountPerCategory,
      bundle.bundleProducts[category]?.length ?? 0
    );

    dictionary[category].minAmountPerCategory = productsCount;
    totalProductsCount += productsCount;
  }
  return { dictionary, productsCount: totalProductsCount };
};

/**
 * Recalculates the bundle composition dictionary so category minimums add up
 * to the required bundle size, following the category prioritization order.
 * Returns null when the bundle has fewer products than the global minimum.
 *
 * @param {Bundle} bundle Bundle data with selected products, their total count,
 *                        and the current composition dictionary.
 * @returns {BundleCompositionDictionary | null} Cloned dictionary with updated
 *          `minAmountPerCategory`, or null if the bundle is undersized.
 */
export const getNewDictionary = (bundle: Bundle): BundleCompositionDictionary | null => {
  if (bundle.sumOfProducts < MINIMUM_PRODUCTS_IN_BUNDLE) return null;

  const normalizedResult = normalizeToMinimumAvailable({ bundle });
  const { dictionary, productsCount } = normalizedResult;
  let currentProductCount = productsCount;

  // iterate by priority, check if current amount is less than it is needed
  // add missing amount per every category until we reach MINIMUM_PRODUCTS_IN_BUNDLE
  for (const category of PRIORITISATION_RANK) {
    // check if we reach necessary minimum threshold
    if (currentProductCount >= MINIMUM_PRODUCTS_IN_BUNDLE) break;

    const availableProductsCount = bundle.bundleProducts[category]?.length ?? 0;
    const minAmountPerCategory = dictionary[category].minAmountPerCategory;
    const missingMinimumAmount = MINIMUM_PRODUCTS_IN_BUNDLE - currentProductCount;
    const toAddPerCategory = availableProductsCount - minAmountPerCategory;
    const productsToAdd = Math.min(toAddPerCategory, missingMinimumAmount);

    if (productsToAdd <= 0) continue;
    dictionary[category].minAmountPerCategory += productsToAdd;
    currentProductCount += productsToAdd;
  }

  return dictionary;
};

const MAX_WET_FOOD_DOG_PRICE = 900;

type EligibleProduct = CollectionProduct & {
  variantsAvailabilityList: AvailableVariant[];
  baseVariant: CollectionVariant;
  productType: BundleCategoryKey;
};

const isEligibleProduct =
  (productTypes: string[]) =>
  (product: CollectionProduct): product is EligibleProduct =>
    CATEGORY_ORDER.includes(product.productType as BundleCategoryKey) &&
    product.variantsAvailabilityList !== undefined &&
    product.baseVariant !== undefined &&
    productTypes.includes(product.productType) &&
    !isBundleProduct(product);

const isVariantEligible =
  (product: EligibleProduct, petType: PetType) =>
  (variant: AvailableVariant): boolean => {
    if (
      petType === PetType.Dog &&
      product.productType === snacks &&
      variant.defaultPrice !== undefined
    ) {
      return variant.available && variant.defaultPrice < MAX_WET_FOOD_DOG_PRICE;
    }
    return variant.available;
  };

interface PickTargetVariantArgs {
  product: EligibleProduct;
  petType: PetType;
  breed?: BreedSegmentationIds;
}

const pickTargetVariant = ({
  product,
  petType,
  breed,
}: PickTargetVariantArgs): AvailableVariant | undefined => {
  const targetWeightGrams = getDogTargetWeight(breed);
  const sorter =
    petType === PetType.Dog && product.productType === wetFood
      ? aimForTargetWeight(targetWeightGrams)
      : sortByWeight;
  return product.variantsAvailabilityList
    .filter(isVariantEligible(product, petType))
    .toSorted(sorter)
    .at(0);
};

export const calculateBundle = ({
  products,
  productTypes,
  bundleCompositionDictionary,
  petType,
  breed,
}: CalculateBundleArgs): Bundle | null => {
  const bundleId = crypto.randomUUID();

  const variants: BundleVariant[] = products
    .filter(isEligibleProduct(productTypes))
    .flatMap((product) => {
      const targetVariant = pickTargetVariant({ product, petType, breed });
      return targetVariant
        ? [
            {
              ...product.baseVariant,
              ...targetVariant,
              id: targetVariant.variantId,
              productTitle: product.title,
              handle: product.handle,
              category: product.productType,
              bundleRefs: [{ bundleId, quantity: 1 }],
            },
          ]
        : [];
    });

  const bundle: Bundle = variants.reduce<Bundle>(
    (acc, variant) => {
      acc.bundleProducts[variant.category]?.push(variant);
      acc.sumOfProducts += 1;
      return acc;
    },
    {
      bundleProducts: createBundleComposition(productTypes),
      sumOfProducts: 0,
      dictionary: bundleCompositionDictionary,
      bundleId,
    }
  );

  if (bundle.sumOfProducts < MINIMUM_PRODUCTS_IN_BUNDLE) return null;

  const canUseDefaultDictionary = CATEGORY_ORDER.every(
    (category) =>
      (bundle.bundleProducts[category]?.length ?? 0) >=
      bundleCompositionDictionary[category].minAmountPerCategory
  );

  // in case the incoming result does not match provided initial dictionary, we need to adapt and change the dictionary
  const dictionary = canUseDefaultDictionary
    ? bundleCompositionDictionary
    : getNewDictionary(bundle);

  if (dictionary === null) return null;

  return {
    ...bundle,
    dictionary,
  };
};

const isSectionTextLike = (s: AnySection): s is SectionTextLike =>
  s?.sys?.contentType?.sys?.id === 'sectionText';

// extract content (ingredients) from contentful sections provided
// as a temporary solution it looks for match with ingredientsTitles and productInformationTab === true
export function getIngredientsContent(
  sections: AnySection[] | undefined,
  ingredientsTitles: readonly string[]
): string | undefined {
  const section = sections
    ?.filter(isSectionTextLike)
    .find(
      (s) =>
        !!s.fields.productInformationTab && ingredientsTitles.includes(s.fields.richTitle ?? '')
    );

  const doc = section?.fields.text;
  const parts =
    doc?.content?.map((node) => documentToPlainTextString(node).trim()).filter(Boolean) ?? [];

  return parts.length ? parts.join('. ') : undefined;
}

type GetAmountsPerCategoryResult = Record<BundleCategoryKey, number>;

export const getAmountsPerCategory = ({
  items,
  bundle,
}: {
  items: BundleVariant[];
  bundle: Bundle;
}): GetAmountsPerCategoryResult => {
  return items.reduce(
    (acc, item) => {
      const quantity = bundle.dictionary[item.category].amountPerItem;
      acc[item.category] += quantity;
      return acc;
    },
    {
      [wetFood]: 0,
      [dryFood]: 0,
      [snacks]: 0,
    }
  );
};

/**
 * Gross-after-bundle-discount total for wet food + snacks, simulated at a
 * given wet food quantity. The discounted `price` on effective-cart items is
 * linear in quantity, so we can project totals at higher wet-food quantities
 * without making additional API calls. Snacks stay at whatever quantity the
 * response holds (they are never adjusted by the default-value guardrail).
 */
export const getWetAndSnacksTotalAtWetQty = (items: PdProductItem[], wetQuantity: number): number =>
  items.reduce((acc, item) => {
    if (item.productType === wetFood) return acc + item.price * wetQuantity;
    if (item.productType === snacks) return acc + item.price * item.quantity;
    return acc;
  }, 0);

interface PickBoostStepArgs {
  /** Effective cart items at the default wet food quantity. */
  items: PdProductItem[];
  /** Current default wet food amountPerItem (before any boost). */
  defaultWetQty: number;
  /** Gross-after-discount threshold in cents (e.g. €29 = 2900). */
  thresholdCents: number;
  /** Ordered candidate wet food quantities to escalate through. */
  escalationSteps: number[];
}

/**
 * Picks the target wet food `amountPerItem` for the default-value guardrail.
 * Returns `null` when no boost is needed — either the bundle has no wet food
 * to boost, or the default-quantity total already clears the threshold. When
 * a boost is needed, returns the smallest step that clears the threshold, or
 * the last escalation step if none do.
 */
export const pickBoostStep = ({
  items,
  defaultWetQty,
  thresholdCents,
  escalationSteps,
}: PickBoostStepArgs): number | null => {
  if (!items.some((item) => item.productType === wetFood)) return null;
  if (getWetAndSnacksTotalAtWetQty(items, defaultWetQty) >= thresholdCents) return null;

  const clearingStep = escalationSteps.find(
    (step) => getWetAndSnacksTotalAtWetQty(items, step) >= thresholdCents
  );
  return clearingStep ?? escalationSteps.at(-1) ?? null;
};

interface IsBundlePriceStaleArgs {
  /** Effective cart items from the pricing hook. */
  items: PdProductItem[];
  /** The boost chosen for the current bundle, or `null` while pending. */
  wetFoodBoost: number | null;
  /** Current default wet food amountPerItem (before any boost). */
  defaultWetQty: number;
  /** Gross-after-discount threshold in cents (e.g. €29 = 2900). */
  thresholdCents: number;
}

/**
 * Returns `true` when the current pricing response does not reflect the
 * wet-food quantity we expect to display — either (a) the boost is still
 * being decided and the default-quantity total is below the threshold, or
 * (b) the boost is chosen but the response is still from the previous
 * level (SWR + debounce serve stale data for a short window after the
 * dictionary changes).
 */
export const isBundlePriceStale = ({
  items,
  wetFoodBoost,
  defaultWetQty,
  thresholdCents,
}: IsBundlePriceStaleArgs): boolean => {
  const expectedWetQty = wetFoodBoost ?? defaultWetQty;
  const pricesReflectExpectedBoost = items.every(
    (item) => item.productType !== wetFood || item.quantity === expectedWetQty
  );
  if (!pricesReflectExpectedBoost) return true;

  // Boost hasn't been decided yet AND wet+snacks at default qty is below
  // threshold → we're about to bump, so the displayed price would flicker.
  const hasWetFood = items.some((item) => item.productType === wetFood);
  const isAwaitingDefaultBoost =
    wetFoodBoost === null &&
    hasWetFood &&
    getWetAndSnacksTotalAtWetQty(items, defaultWetQty) < thresholdCents;
  return isAwaitingDefaultBoost;
};

export interface BundleSubmit {
  items: BundleVariant[];
  currentBundle: Bundle;
  bundleName: string;
  hasPetName: boolean;
}

export type BundlePurchaseType = 'subscription' | 'one-time';

export type AddBundleToCartArgs = BundleSubmit & {
  purchaseType: BundlePurchaseType;
};

export const getDefaultAmountsPerCategory = (bundle: Bundle): Record<BundleCategoryKey, number> =>
  CATEGORY_ORDER.reduce(
    (acc, category) => {
      const products = bundle.bundleProducts[category] || [];
      acc[category] = Math.min(bundle.dictionary[category].minAmountPerCategory, products.length);
      return acc;
    },
    {} as Record<BundleCategoryKey, number>
  );

export const buildBundleSubmitPayload = ({
  bundle,
  amountPerCategory,
  bundleId,
  bundleName,
  hasPetName,
}: {
  bundle: Bundle;
  amountPerCategory: Record<BundleCategoryKey, number>;
  bundleId: string;
  bundleName: string;
  hasPetName: boolean;
}): BundleSubmit => {
  const items = CATEGORY_ORDER.flatMap((category) => {
    const amount = amountPerCategory[category] ?? 0;
    const products = bundle.bundleProducts[category] || [];
    return products.slice(0, amount).map((variant) => ({
      ...variant,
      category,
      bundleRefs: [{ bundleId, quantity: 1 }],
    }));
  });

  return { items, currentBundle: { ...bundle, bundleId }, bundleName, hasPetName };
};

/** Builds the submit payload for the bundle's default composition (every category at its minAmountPerCategory). */
export const buildDefaultBundleSubmit = (
  bundle: Bundle,
  bundleName: string,
  hasPetName: boolean
): BundleSubmit =>
  buildBundleSubmitPayload({
    bundle,
    amountPerCategory: getDefaultAmountsPerCategory(bundle),
    bundleId: crypto.randomUUID(),
    bundleName,
    hasPetName,
  });
